import { Icon } from "@iconify/react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Hide scrollbar CSS
const hideScrollbarStyle = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
};

const Color = {
  pdfBlack: "#1a1a1a",
  themeBlue: "#4ea4f5",
  textThemeDark: "#226fb7", // For accents
  textPrimary: "#000",
  textSecondary: "#666",
  textHintStrong: "#888",
  borderLight: "#e0e0e0",
  lineDivider: "#eee",
  white: "#fff",
  highlightYellow: "#fff3cd",
  bgLight: "#f8f9fa",
  gray300: "#d1d5db",
  // Difficulty colors
  difficultyHard: "#ef4444",
  difficultyMedium: "#eab308",
  difficultyEasy: "#22c55e",
  // Chat colors
  userBubble: "#f5f5f5",
  aiBubble: "#fafafa",
};

// Content IDs for highlighting
type HighlightTarget = "title" | "intro" | "equation" | "graph" | "conclusion" | null;

interface FeatureSectionPDFProps {
  showDescription?: boolean;
  autoStart?: boolean;
}

const FeatureSectionPDF: React.FC<FeatureSectionPDFProps> = ({ showDescription = true, autoStart = false }) => {
  const { t } = useTranslation();
  // Animation phases like NotebookLM: pdfDrop -> folder -> folder-click -> uploadZone -> sourcesLoading -> inputTyping -> aiResponse -> scanning -> split
  const [phase, setPhase] = useState<
    "pdfDrop" | "folder" | "folder-click" | "uploadZone" | "sourcesLoading" | "inputTyping" | "aiResponse" | "scanning" | "split"
  >("folder");
  const [pdfSplitPhase, setPdfSplitPhase] = useState<"dropping" | "splitting" | "done">("dropping");
  const [showHighlight, setShowHighlight] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [streamingIndex, setStreamingIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("sections");
  const [highlightTarget, setHighlightTarget] = useState<HighlightTarget>(null);
  const [clickedSection, setClickedSection] = useState<string | null>(null);
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);
  const [chatStage, setChatStage] = useState<"input" | "sent">("input");
  const [isOverviewCitationExpanded, setIsOverviewCitationExpanded] = useState(false);
  const [showBulletPoints, setShowBulletPoints] = useState(autoStart);
  const [bulgingElement, setBulgingElement] = useState<string | null>(null);

  // NotebookLM-style animation states
  const [loadedSources, setLoadedSources] = useState<number[]>([]); // Which sources have loaded
  const [typedQuestion, setTypedQuestion] = useState(""); // Typing animation for question
  const [aiStreamText, setAiStreamText] = useState(""); // AI response streaming
  const [scanningProgress, setScanningProgress] = useState(0); // Finding key points progress

  // "Finding key points" loading state
  const [showFindingKeyPoints, setShowFindingKeyPoints] = useState(false);
  const [visibleSections, setVisibleSections] = useState<string[]>([]); // Which sections have dashed in

  const pdfScrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationAbortRef = useRef<boolean>(false);
  const animationStartedRef = useRef<boolean>(false);
  const bulletPointsShownRef = useRef<boolean>(autoStart);

  // Trigger animation as soon as the section is partially visible
  const inViewResult = useInView(cardRef, { amount: 0.3 });
  const isInView = autoStart || inViewResult;

  const sourceCards = [
    { icon: "lucide:file-text", color: "#ef4444", label: "Research PDF", type: "pdf" },
    { icon: "logos:youtube-icon", color: "#ff0000", label: "YouTube lecture", type: "youtube" },
    { icon: "lucide:mic", color: "#4ea4f5", label: "Meeting audio", type: "audio" },
    { icon: "simple-icons:slack", color: "#611f69", label: "Slack thread", type: "slack" },
    { icon: "lucide:globe", color: "#22c55e", label: "Web article", type: "web" },
  ];

  // Question to type
  const questionText = t("modern.featureSections.pdf.demoQuestion") || "What should I know across these materials?";

  // AI response for streaming
  const aiDemoResponse = t("modern.featureSections.pdf.demoAiResponse") || "Based on your materials, I found the core decisions across the PDF, lecture video, meeting audio, Slack thread, and web article, with citations back to each source.";

  // Handle card click - manually trigger full animation loop
  const handleCardClick = async () => {
    // Reset to initial state
    setPhase("pdfDrop");
    setPdfSplitPhase("dropping");
    setShowHighlight(false);
    setShowToolbar(false);
    setShowAIChat(false);
    setStreamingIndex(0);
    setActiveTab("sections");
    setHighlightTarget(null);
    setClickedSection(null);
    setChatStage("input");
    setIsOverviewCitationExpanded(false);
    setBulgingElement(null);

    // Reset PDF scroll
    if (pdfScrollRef.current) {
      pdfScrollRef.current.scrollTo({ top: 0 });
    }

    // Small delay
    await new Promise(r => setTimeout(r, 300));

    // STAGE 0: PDF Drop animation
    await new Promise(r => setTimeout(r, 800));
    setPdfSplitPhase("splitting");
    await new Promise(r => setTimeout(r, 1200));
    setPdfSplitPhase("done");
    await new Promise(r => setTimeout(r, 600));

    // STAGE 1: Show folder briefly then press into split view
    setPhase("folder");
    await new Promise(r => setTimeout(r, 800));
    setPhase("split");
    await new Promise(r => setTimeout(r, 1500));

    // STAGE 2: Press on sections to highlight
    handleHighlight("equation", "section-0");
    await new Promise(r => setTimeout(r, 800));

    handleHighlight("graph", "sub-1-0");
    await new Promise(r => setTimeout(r, 800));

    // STAGE 3: Scroll and show highlight + toolbar
    if (pdfScrollRef.current) {
      const targetElement = pdfScrollRef.current.querySelector(`[data-highlight="askAiText"]`);
      if (targetElement) {
        const container = pdfScrollRef.current;
        const containerHeight = container.clientHeight;
        const elementTop = (targetElement as HTMLElement).offsetTop;
        const elementHeight = (targetElement as HTMLElement).offsetHeight;
        const scrollPosition = elementTop - (containerHeight / 2) + (elementHeight / 2) + 20;
        container.scrollTo({ top: Math.max(0, scrollPosition), behavior: "smooth" });
      }
    }
    await new Promise(r => setTimeout(r, 500));

    setShowHighlight(true);
    setShowToolbar(true);
    await new Promise(r => setTimeout(r, 600));

    // STAGE 4: Click Ask AI
    setShowToolbar(false);
    setShowHighlight(false);
    setActiveTab("chat");
    setChatStage("sent");
    setShowAIChat(true);
    await new Promise(r => setTimeout(r, 500));

    // Stream AI response
    for (let i = 0; i <= aiResponseWords.length; i++) {
      setStreamingIndex(i);
      await new Promise(r => setTimeout(r, 40));
    }

    // Animation complete - show bullet points
    await new Promise(r => setTimeout(r, 1000));
    setShowBulletPoints(true);
    bulletPointsShownRef.current = true;
  };

  const materialParts = [
    { icon: "lucide:file-text", color: "#ef4444", name: "Research Paper.pdf", date: "PDF · 28 pages", pages: "P1-8" },
    { icon: "logos:youtube-icon", color: "#ff0000", name: "Strategy lecture", date: "YouTube · 42 min", pages: "12:40" },
    { icon: "lucide:mic", color: "#4ea4f5", name: "Weekly planning audio", date: "Audio · 31 min", pages: "08:15" },
    { icon: "simple-icons:slack", color: "#611f69", name: "Launch thread", date: "Slack · 18 messages", pages: "#team" },
  ];

  // Tabs with translation keys
  const tabs = [
    { id: "summary", labelKey: "landing.featureDetail.pdf.mockup.tabs.summary" },
    { id: "chat", labelKey: "landing.featureDetail.pdf.mockup.tabs.chat" },
    { id: "sections", labelKey: "landing.featureDetail.pdf.mockup.tabs.sections" },
  ];

  // Summary content with inline citations
  const summaryContent = {
    overviewKey: "modern.featureSections.pdf.summaryOverview",
    overviewCitations: ["1", "2"], // Multiple pages - shows as "1页 +1" when collapsed, expands to show both
    keyPoints: [
      {
        textKey: "modern.featureSections.pdf.keyPoint0",
        citation: { page: "2" },
        target: "equation" as HighlightTarget,
      },
      {
        textKey: "modern.featureSections.pdf.keyPoint1",
        citation: { page: "3" },
        target: "graph" as HighlightTarget,
      },
      {
        textKey: "modern.featureSections.pdf.keyPoint2",
        citation: { page: "4" },
        target: "conclusion" as HighlightTarget,
      },
    ],
  };

  // Sections with targets for highlighting (keep original)
  const sectionsContent = [
    {
      titleKey: "modern.featureSections.pdf.section0Title",
      subtopics: 2,
      pageRef: "P2",
      expanded: true,
      descriptionKey: "modern.featureSections.pdf.section0Description",
      target: "equation" as HighlightTarget,
      subsections: [
        { titleKey: "modern.featureSections.pdf.section0Sub0Title", descriptionKey: "modern.featureSections.pdf.section0Sub0Description", pageRef: "P2", difficulty: "hard", target: "equation" as HighlightTarget },
        { titleKey: "modern.featureSections.pdf.section0Sub1Title", descriptionKey: "modern.featureSections.pdf.section0Sub1Description", pageRef: "P2", difficulty: "medium", target: "intro" as HighlightTarget },
      ],
    },
    {
      titleKey: "modern.featureSections.pdf.section1Title",
      subtopics: 2,
      pageRef: "P3-4",
      expanded: true,
      descriptionKey: "modern.featureSections.pdf.section1Description",
      target: "graph" as HighlightTarget,
      subsections: [
        { titleKey: "modern.featureSections.pdf.section1Sub0Title", descriptionKey: "modern.featureSections.pdf.section1Sub0Description", pageRef: "P3", difficulty: "easy", target: "graph" as HighlightTarget },
        { titleKey: "modern.featureSections.pdf.section1Sub1Title", descriptionKey: "modern.featureSections.pdf.section1Sub1Description", pageRef: "P4", difficulty: "hard", target: "conclusion" as HighlightTarget },
      ],
    },
  ];

  // AI chat response
  const aiResponse = t("modern.featureSections.pdf.aiResponseKey");
  const aiResponseWords = aiResponse.split(" ");

  // Handle citation/section click - highlight and scroll ONLY within PDF container
  const handleHighlight = (target: HighlightTarget, sectionId?: string) => {
    // Trigger click ripple animation
    if (sectionId) {
      setClickedSection(sectionId);
      setTimeout(() => setClickedSection(null), 600);
    }

    setHighlightTarget(target);
    // Scroll ONLY the PDF container, not the page
    if (pdfScrollRef.current && target) {
      const targetElement = pdfScrollRef.current.querySelector(`[data-highlight="${target}"]`);
      if (targetElement) {
        const container = pdfScrollRef.current;
        const elementTop = (targetElement as HTMLElement).offsetTop;
        container.scrollTo({ top: elementTop - 20, behavior: "smooth" });
      }
    }
    // Clear highlight after 3 seconds
    setTimeout(() => setHighlightTarget(null), 3000);
  };

  // Animation sequence - coordinated stages with pause/resume support, only when in view
  useEffect(() => {
    // Don't start animation if not in view
    if (!isInView) {
      animationStartedRef.current = false;
      return;
    }

    // Prevent double-run
    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    let isMounted = true;

    // Helper to wait with abort check
    const wait = (ms: number) => new Promise<boolean>(resolve => {
      const checkAbort = () => {
        if (animationAbortRef.current || !isMounted) {
          resolve(false);
          return;
        }
        resolve(true);
      };
      setTimeout(checkAbort, ms);
    });

    const runAnimation = async () => {
      // Don't start if paused or not in view
      if (isAnimationPaused || animationAbortRef.current || !isInView) return;

      // Reset card state — skip pdfDrop, start at folder
      setPhase("folder");
      setPdfSplitPhase("done");
      setShowHighlight(false);
      setShowToolbar(false);
      setShowAIChat(false);
      setStreamingIndex(0);
      setActiveTab("sections");
      setHighlightTarget(null);
      setClickedSection(null);
      setChatStage("input");
      setIsOverviewCitationExpanded(false);
      // Only hide bullet points if they haven't been shown yet
      if (!bulletPointsShownRef.current) {
        setShowBulletPoints(false);
      }
      setBulgingElement(null);
      setShowFindingKeyPoints(false);
      setVisibleSections([]);

      // Wait before interaction
      if (!await wait(750)) return;
      if (!await wait(500)) return;

      // STAGE 2: Bulge on Part 1 row before clicking (show hint)
      setBulgingElement("part-0");
      if (!await wait(1000)) return;

      // Press into split view
      setBulgingElement(null);
      setPhase("split");
      if (!await wait(300)) return;

      // STAGE 2.5: Show "Finding key points" loading animation
      setShowFindingKeyPoints(true);
      if (!await wait(2000)) return;
      setShowFindingKeyPoints(false);

      // STAGE 2.6: Dash in sections one by one
      // First section header
      setVisibleSections(["section-0"]);
      if (!await wait(400)) return;
      // First section subsections
      setVisibleSections(prev => [...prev, "sub-0-0", "sub-0-1"]);
      if (!await wait(500)) return;
      // Second section header
      setVisibleSections(prev => [...prev, "section-1"]);
      if (!await wait(400)) return;
      // Second section subsections
      setVisibleSections(prev => [...prev, "sub-1-0", "sub-1-1"]);
      if (!await wait(800)) return;

      // STAGE 3: Press on sections to highlight (faster transitions)
      // Bulge on first section before clicking
      setBulgingElement("section-0");
      if (!await wait(400)) return;

      // Press first section - highlight equation
      setBulgingElement(null);
      handleHighlight("equation", "section-0");
      if (!await wait(600)) return;

      // Bulge on second subsection before clicking
      setBulgingElement("sub-1-0");
      if (!await wait(400)) return;

      // Press second subsection - highlight graph
      setBulgingElement(null);
      handleHighlight("graph", "sub-1-0");
      if (!await wait(600)) return;

      // STAGE 4: First scroll to center the highlighted text in view, then highlight + show toolbar
      if (pdfScrollRef.current) {
        const targetElement = pdfScrollRef.current.querySelector(`[data-highlight="askAiText"]`);
        if (targetElement) {
          const container = pdfScrollRef.current;
          const containerHeight = container.clientHeight;
          const elementTop = (targetElement as HTMLElement).offsetTop;
          const elementHeight = (targetElement as HTMLElement).offsetHeight;
          // Center the element in the container (accounting for toolbar space above)
          const scrollPosition = elementTop - (containerHeight / 2) + (elementHeight / 2) + 20;
          container.scrollTo({ top: Math.max(0, scrollPosition), behavior: "smooth" });
        }
      }
      if (!await wait(500)) return;

      // Now show highlight and toolbar together
      setShowHighlight(true);
      setShowToolbar(true);
      if (!await wait(600)) return;

      // Bulge on Ask AI button before clicking (faster)
      setBulgingElement("ask-ai-button");
      if (!await wait(800)) return;

      // STAGE 5: Click Ask AI - switch to chat tab and show AI response immediately
      setBulgingElement(null);
      setShowToolbar(false);
      setShowHighlight(false);
      setActiveTab("chat");
      setChatStage("sent");
      setShowAIChat(true);
      if (!await wait(500)) return;

      // Stream AI response
      for (let i = 0; i <= aiResponseWords.length; i++) {
        if (animationAbortRef.current || !isMounted) return;
        setStreamingIndex(i);
        await new Promise(r => setTimeout(r, 40));
      }

      // Wait a bit after streaming completes
      if (!await wait(1000)) return;

      // Animation complete - show bullet points and mark as shown
      setShowBulletPoints(true);
      bulletPointsShownRef.current = true;

      // Wait for reading bullet points (~5 seconds)
      if (!await wait(5000)) return;

      // Wait before restarting loop (8 seconds)
      if (!await wait(8000)) return;

      // Reset highlights
      setShowHighlight(false);
    };

    // Continuous rotation - animation runs back to back
    const animationLoop = async () => {
      while (isMounted) {
        if (!isAnimationPaused && !animationAbortRef.current) {
          await runAnimation();
        }
        // Small delay before next cycle
        await new Promise(r => setTimeout(r, 500));
      }
    };

    animationLoop();

    return () => {
      isMounted = false;
      animationStartedRef.current = false;
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, [isAnimationPaused, isInView]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "hard": return Color.difficultyHard;
      case "medium": return Color.difficultyMedium;
      case "easy": return Color.difficultyEasy;
      default: return Color.difficultyMedium;
    }
  };

  const getHighlightStyle = (target: HighlightTarget) => {
    if (highlightTarget === target) {
      return {
        backgroundColor: Color.highlightYellow,
        boxShadow: `0 0 0 2px ${Color.themeBlue}40, 0 2px 8px ${Color.themeBlue}20`,
        borderRadius: "4px",
        transition: "none", // Instant highlight
      };
    }
    return {
      transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    };
  };

  // Citation badge component - pill shape with light blue background
  const CitationBadge = ({ page, onClick }: { page: string; onClick?: () => void }) => (
    <span
      className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full ml-1 ${onClick ? "cursor-pointer hover:bg-blue-100" : ""}`}
      style={{
        backgroundColor: `${Color.themeBlue}15`,
        color: Color.themeBlue,
      }}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    >
      {page}页
    </span>
  );

  return (
    <section className={`${showDescription ? (autoStart ? 'py-4 md:py-8' : 'py-8 md:py-20') : 'py-0'} bg-white overflow-hidden`}>
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div className={`${showDescription ? 'grid md:grid-cols-2 gap-10 lg:gap-14 items-start max-w-7xl mx-auto' : 'flex justify-center'}`} style={showDescription ? { minHeight: "auto" } : undefined}>
          {/* Text Content - Left on desktop */}
          {showDescription && <motion.div
            initial={{ opacity: 0, x: -50 }}
            {...(autoStart ? { animate: { opacity: 1, x: 0 } } : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
            transition={{ duration: 0.6 }}
            className="order-1 flex flex-col"
          >
            {/* Top group */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ backgroundColor: `${Color.themeBlue}15` }}
              >
                <Icon icon="lucide:layers-3" style={{ color: Color.themeBlue }} />
                <span style={{ color: Color.themeBlue }} className="font-medium text-sm">
                  {t("landing.featureDetail.pdf.title")}
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight mt-0">
                {t("landing.features.pdf.headline")}
              </h2>

              <p className="text-lg md:text-xl leading-relaxed" style={{ color: "#726F6F" }}>
                {t("landing.features.pdf.description")}
              </p>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-[16px] max-h-[32px]" />

            {/* Bottom group: bullets */}
            <div className="block md:hidden space-y-4">
              {[
                { icon: "lucide:upload", textKey: "landing.features.pdf.bullets.upload" },
                { icon: "lucide:radio", textKey: "landing.features.pdf.bullets.split" },
                { icon: "lucide:mouse-pointer-click", textKey: "landing.features.pdf.bullets.citations" },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${Color.themeBlue}15` }}
                  >
                    <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                  </div>
                  <span className="text-base text-gray-700">{t(item.textKey)}</span>
                </div>
              ))}
            </div>
            <div className="hidden md:block">
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: showBulletPoints ? 1 : 0 }}
                transition={{ duration: 0.4 }}
              >
                {[
                  { icon: "lucide:upload", textKey: "landing.features.pdf.bullets.upload" },
                  { icon: "lucide:radio", textKey: "landing.features.pdf.bullets.split" },
                  { icon: "lucide:mouse-pointer-click", textKey: "landing.features.pdf.bullets.citations" },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: showBulletPoints ? 1 : 0, x: showBulletPoints ? 0 : 20 }}
                    transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.themeBlue}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                    </div>
                    <span className="text-base text-gray-700">{t(item.textKey)}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>}

          {/* Visual Mockup - Right on desktop */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            {...(autoStart ? { animate: { opacity: 1, x: 0 } } : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`${showDescription ? "order-2" : ""} px-4 md:px-0 flex flex-col justify-end`}
            style={{ paddingTop: "68px" }}
          >
            <div className="relative w-full max-w-[580px] mx-auto">
              {/* PDF Interface Mockup */}
              <div
                ref={cardRef}
                className="rounded-3xl overflow-hidden shadow-2xl"
                style={{
                  backgroundColor: Color.white,
                  border: `1px solid ${Color.borderLight}`,
                  height: 380,
                }}
                onClick={handleCardClick}
              >
              {/* Scaled inner content */}
              <div className="origin-top-left" style={{ transform: "scale(0.82)", width: "122%", height: "122%" }}>
              {/* Main Content Area */}
              <div style={{ height: 463 }}>
                <AnimatePresence mode="wait">
                  {phase === "pdfDrop" ? (
                    /* PDF Drop Animation - Shows a large PDF dropping and splitting into chapters */
                    <motion.div
                      key="pdfDrop"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="h-full flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
                    >
                      {/* Background subtle grid pattern */}
                      <div className="absolute inset-0 opacity-5" style={{
                        backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }} />

                      {/* Central mixed-source animation container */}
                      <div className="relative flex flex-col items-center">
                        {pdfSplitPhase === "dropping" && (
                          <motion.div
                            className="flex flex-col items-center"
                            initial={{ y: -100, opacity: 0, scale: 0.8 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            transition={{
                              type: "spring" as const,
                              stiffness: 200,
                              damping: 20
                            }}
                          >
                            <div className="grid grid-cols-2 gap-3">
                              {sourceCards.slice(0, 4).map((source) => (
                                <div
                                  className="flex h-24 w-28 flex-col items-center justify-center rounded-xl px-2 text-center shadow-xl"
                                  key={source.type}
                                  style={{
                                    backgroundColor: source.color,
                                    boxShadow: `0 20px 38px -18px ${source.color}`,
                                  }}
                                >
                                  <Icon icon={source.icon} className="mb-1 text-white text-3xl" />
                                  <span className="text-[10px] font-medium leading-tight text-white">{source.label}</span>
                                  <span className="text-[9px] text-white/70">{source.type.toUpperCase()}</span>
                                </div>
                              ))}
                            </div>
                            <motion.div
                              className="mt-6 flex items-center gap-2"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5, duration: 0.4 }}
                            >
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <Icon icon="lucide:loader-2" className="text-gray-400 text-sm" />
                              </motion.div>
                              <span className="text-sm text-gray-500">{t("modern.featureSections.pdf.mockContent.importingDocument")}</span>
                            </motion.div>
                          </motion.div>
                        )}

                        {pdfSplitPhase === "splitting" && (
                          /* Sources normalize into AI-readable cards */
                          <div className="flex flex-col items-center">
                            <div className="flex items-end gap-3">
                              {sourceCards.slice(0, 4).map((source, index) => (
                                <motion.div
                                  key={source.type}
                                  className="w-24 h-32 rounded-lg shadow-xl flex flex-col items-center justify-center px-2 text-center"
                                  style={{
                                    backgroundColor: source.color,
                                  }}
                                  initial={{
                                    x: 0,
                                    y: 0,
                                    opacity: 0,
                                    scale: 0.5,
                                    rotate: 0
                                  }}
                                  animate={{
                                    x: (index - 1) * 10,
                                    y: index === 1 ? -10 : 0,
                                    opacity: 1,
                                    scale: 1,
                                    rotate: (index - 1) * 3
                                  }}
                                  transition={{
                                    delay: index * 0.15,
                                    type: "spring" as const,
                                    stiffness: 300,
                                    damping: 20
                                  }}
                                >
                                  <Icon icon={source.icon} className="text-white text-3xl mb-1" />
                                  <span className="text-white text-[10px] font-medium leading-tight">{source.label}</span>
                                  <span className="text-white/70 text-[9px]">{source.type.toUpperCase()}</span>
                                </motion.div>
                              ))}
                            </div>
                            <motion.div
                              className="mt-6 flex items-center gap-2"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.5 }}
                            >
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <Icon icon="lucide:loader-2" className="text-gray-400 text-sm" />
                              </motion.div>
                              <span className="text-sm text-gray-500">{t("modern.featureSections.pdf.mockContent.splittingIntoChapters")}</span>
                            </motion.div>
                          </div>
                        )}

                        {pdfSplitPhase === "done" && (
                          /* Chapters settled with success indicator */
                          <div className="flex flex-col items-center">
                            <div className="flex items-end gap-3">
                              {sourceCards.slice(0, 4).map((source, index) => (
                                <motion.div
                                  key={source.type}
                                  className="w-24 h-32 rounded-lg shadow-xl flex flex-col items-center justify-center px-2 text-center"
                                  style={{
                                    backgroundColor: source.color,
                                  }}
                                  initial={{ rotate: (index - 1) * 3 }}
                                  animate={{
                                    rotate: 0,
                                    y: [0, -5, 0]
                                  }}
                                  transition={{
                                    delay: index * 0.1,
                                    duration: 0.5
                                  }}
                                >
                                  <Icon icon={source.icon} className="text-white text-3xl mb-1" />
                                  <span className="text-white text-[10px] font-medium leading-tight">{source.label}</span>
                                  <span className="text-white/70 text-[9px]">{source.type.toUpperCase()}</span>
                                </motion.div>
                              ))}
                            </div>
                            <motion.div
                              className="mt-6 flex items-center gap-2"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                            >
                              <motion.div
                                className="w-6 h-6 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: "#22c55e" }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring" as const, stiffness: 400, damping: 15 }}
                              >
                                <Icon icon="lucide:check" className="text-white text-sm" />
                              </motion.div>
                              <span className="text-sm text-gray-700 font-medium">{t("modern.featureSections.pdf.mockContent.splitIntoParts")}</span>
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ) : phase === "folder" ? (
                    /* Folder View - Minimalist, no header */
                    <motion.div
                      key="folder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, x: -30 }}
                      className="h-full flex flex-col"
                    >
                      {/* Main Content - No top bar */}
                      <div className="flex-1 px-6 py-6 bg-white">
                        {/* Title Row with Action Buttons */}
                        <div className="flex items-center justify-between mb-0.5">
                          <h3 className="text-xl font-bold text-gray-300">{t("modern.featureSections.pdf.mockContent.fileName")}</h3>
                          <div className="flex items-center gap-2">
                            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" onClick={(e) => e.stopPropagation()}>
                              <Icon icon="lucide:sparkles" className="text-sm" />
                              {t("landing.featureDetail.pdf.mockup.aiTools")}
                            </button>
                            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" onClick={(e) => e.stopPropagation()}>
                              <Icon icon="lucide:plus" className="text-sm" />
                              {t("landing.featureDetail.pdf.mockup.newNote")}
                            </button>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-300 mb-5">
                          {t("landing.featureDetail.pdf.mockup.description")}
                        </p>

                        {/* Parts List */}
                        <div className="space-y-0">
                          {materialParts.map((part, index) => (
                            <motion.div
                              key={index}
                              className="flex items-center gap-4 py-3 px-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-all"
                              initial={{ opacity: 0, y: 10 }}
                              animate={
                                bulgingElement === `part-${index}`
                                  ? { opacity: 1, y: 0, scale: [1, 1.03, 1.03], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 15px rgba(78, 164, 245, 0.3)", "0 0 15px rgba(78, 164, 245, 0.3)"] }
                                  : { opacity: 1, y: 0, scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                              }
                              transition={bulgingElement === `part-${index}` ? { duration: 0.5, ease: "easeOut" } : { delay: index * 0.1 }}
                              whileHover={{ scale: 1.005 }}
                              onClick={(e) => { e.stopPropagation(); setPhase("split"); }}
                            >
                              <Icon icon={part.icon} className="text-lg flex-shrink-0" style={{ color: part.color }} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900 leading-tight">{part.name}</p>
                                <p className="text-xs text-gray-300 leading-tight mt-0.5">{part.date}</p>
                              </div>
                              <Icon icon="lucide:folder" className="text-gray-200 text-lg flex-shrink-0" />
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    /* Split View - PDF on LEFT, Tabs on RIGHT (original order) */
                    <motion.div
                      key="split"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="h-full flex flex-col"
                    >
                      {/* Header */}
                      <div
                        className="px-4 py-3 flex items-center gap-2 flex-shrink-0"
                        style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                      >
                        <button
                          className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                          onClick={(e) => { e.stopPropagation(); setPhase("folder"); }}
                        >
                          <Icon icon="lucide:arrow-left" className="text-gray-500" />
                        </button>
                        <Icon icon={materialParts[0].icon} className="text-red-400" style={{ color: materialParts[0].color }} />
                        <span className="font-medium text-gray-700 text-sm">{materialParts[0].name}</span>
                        <span className="text-xs text-gray-400 ml-auto">{materialParts[0].pages}</span>
                      </div>

                      {/* Main split content - PDF LEFT, Tabs RIGHT */}
                      <div className="flex-1 flex min-h-0">
                        {/* Left: PDF Content with its own scroll - No toolbar for minimalism */}
                        <div className="w-1/2 flex flex-col p-3">
                          <div
                            className="flex-1 flex flex-col bg-white rounded-lg overflow-hidden"
                            style={{ border: `1px solid ${Color.borderLight}` }}
                          >
                            {/* PDF content - Clean scroll area without toolbar */}
                            <div
                              ref={pdfScrollRef}
                              className="flex-1 overflow-y-auto p-4"
                              style={hideScrollbarStyle}
                            >
                              {/* Title */}
                              <div data-highlight="title" style={getHighlightStyle("title")} className="rounded px-1 -mx-1">
                                <h3 className="text-base font-bold text-gray-900 mb-1">{t("modern.featureSections.pdf.mockContent.title")}</h3>
                                <p className="text-[11px] text-gray-500 mb-3 italic">{t("modern.featureSections.pdf.mockContent.source")}</p>
                              </div>

                              {/* Intro */}
                              <div data-highlight="intro" style={getHighlightStyle("intro")} className="rounded px-1 -mx-1 mb-3">
                                <p className="text-[13px] text-gray-600 leading-relaxed">
                                  {t("modern.featureSections.pdf.mockContent.introText")}
                                </p>
                              </div>

                              {/* Regular text - no highlight */}
                              <p className="text-[13px] text-gray-600 leading-relaxed mb-3">
                                {t("modern.featureSections.pdf.mockContent.engagementFactorText")}
                              </p>

                              {/* Equation - highlightable */}
                              <div data-highlight="equation" style={getHighlightStyle("equation")} className="my-3 py-3 px-3 bg-gray-50 rounded text-center">
                                <p className="font-mono text-[13px] text-gray-800">{t("modern.featureSections.pdf.mockContent.equation")}</p>
                                <p className="text-[10px] text-gray-500 mt-1">{t("modern.featureSections.pdf.mockContent.equationLabel")}</p>
                              </div>

                              {/* Highlighted sentence for Ask AI - positioned after equation for better centering */}
                              <div data-highlight="askAiText" className="relative mb-3">
                                {/* Ask AI Toolbar - appears above highlighted text */}
                                <AnimatePresence>
                                  {showToolbar && (
                                    <motion.div
                                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      exit={{ opacity: 0, y: 5, scale: 0.95 }}
                                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                      className="absolute -top-9 left-0 flex items-center gap-1 px-1.5 py-1 shadow-lg z-10"
                                      style={{ backgroundColor: Color.white, borderRadius: 8, border: `1px solid ${Color.borderLight}` }}
                                    >
                                      {/* Ask AI button with bulge preview + pulsing animation */}
                                      <motion.button
                                        className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white"
                                        style={{ borderRadius: 6, backgroundColor: Color.themeBlue }}
                                        animate={
                                          bulgingElement === "ask-ai-button"
                                            ? { scale: [1, 1.15, 1.15], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 20px rgba(78, 164, 245, 0.5)", "0 0 20px rgba(78, 164, 245, 0.5)"] }
                                            : { scale: [1, 1.08, 1, 1.08, 1], boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                                        }
                                        transition={
                                          bulgingElement === "ask-ai-button"
                                            ? { duration: 0.5, ease: "easeOut" }
                                            : { duration: 1.5, repeat: Infinity, repeatDelay: 0.5 }
                                        }
                                        whileTap={{ scale: 0.98 }}
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent triggering card click
                                          setActiveTab("chat");
                                          setChatStage("sent");
                                          setShowAIChat(true);
                                        }}
                                      >
                                        <Icon icon="lucide:sparkles" className="text-[11px]" />
                                        {t("modern.featureSections.pdf.mockContent.askAi")}
                                      </motion.button>
                                      <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors" style={{ borderRadius: 6 }} onClick={(e) => e.stopPropagation()}>
                                        <Icon icon="lucide:highlighter" className="text-[11px]" />
                                      </button>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                                <p className="text-[13px] text-gray-600 leading-relaxed">
                                  <motion.span
                                    className="relative inline"
                                    style={{
                                      backgroundColor: showHighlight ? `${Color.themeBlue}30` : "transparent",
                                      padding: showHighlight ? "2px 4px" : "0",
                                      borderRadius: "4px",
                                      boxShadow: showHighlight ? `0 0 0 2px ${Color.themeBlue}40` : "none",
                                    }}
                                  >
                                    {t("modern.featureSections.pdf.mockContent.equationExplanation")}
                                  </motion.span>
                                </p>
                              </div>

                              {/* Graph - highlightable */}
                              <div data-highlight="graph" style={getHighlightStyle("graph")} className="my-3 p-3 bg-gray-50 rounded">
                                <p className="text-[11px] text-gray-500 mb-2 font-medium">{t("modern.featureSections.pdf.mockContent.chartTitle")}</p>
                                <div className="flex items-end justify-center gap-6 h-16">
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="flex items-end gap-1 h-12">
                                      {[20, 45, 65, 45, 20].map((h, i) => (
                                        <div key={i} className="w-2 bg-gray-400 rounded-t" style={{ height: `${h}%` }} />
                                      ))}
                                    </div>
                                    <span className="text-[10px] text-gray-500">{t("modern.featureSections.pdf.mockContent.chartBeforeLabel")}</span>
                                    <span className="text-[9px] text-gray-400">{t("modern.featureSections.pdf.mockContent.chartBeforeMean")}</span>
                                  </div>
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="flex items-end gap-1 h-12">
                                      {[15, 35, 85, 35, 15].map((h, i) => (
                                        <div key={i} className="w-2 rounded-t" style={{ height: `${h}%`, backgroundColor: Color.themeBlue }} />
                                      ))}
                                    </div>
                                    <span className="text-[10px] text-gray-500">{t("modern.featureSections.pdf.mockContent.chartAfterLabel")}</span>
                                    <span className="text-[9px] text-gray-400">{t("modern.featureSections.pdf.mockContent.chartAfterMean")}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Conclusion */}
                              <div data-highlight="conclusion" style={getHighlightStyle("conclusion")} className="rounded px-1 -mx-1">
                                <p className="text-[13px] text-gray-600 leading-relaxed">
                                  {t("modern.featureSections.pdf.mockContent.conclusionText")}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right: Tabs Panel */}
                        <div className="w-1/2 flex flex-col bg-white border-l" style={{ borderColor: Color.borderLight }}>
                          {/* Tabs */}
                          <div className="flex items-center px-2 py-2 gap-1 overflow-x-auto flex-shrink-0" style={hideScrollbarStyle}>
                            {tabs.map((tab) => (
                              <button
                                key={tab.id}
                                className={`px-3 py-1.5 text-[10px] font-medium rounded-full whitespace-nowrap transition-colors ${
                                  activeTab === tab.id
                                    ? "text-white"
                                    : "text-gray-600 hover:bg-gray-50"
                                }`}
                                style={{
                                  backgroundColor: activeTab === tab.id ? Color.themeBlue : "transparent",
                                  border: activeTab === tab.id ? "none" : `1px solid ${Color.borderLight}`,
                                }}
                                onClick={(e) => { e.stopPropagation(); setActiveTab(tab.id); }}
                              >
                                {t(tab.labelKey)}
                              </button>
                            ))}
                          </div>

                          {/* Tab Content */}
                          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                            <AnimatePresence mode="wait">
                              {activeTab === "summary" ? (
                                /* Summary Tab with citation badges */
                                <motion.div
                                  key="summary"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="p-4 space-y-4 overflow-y-auto flex-1"
                                  style={hideScrollbarStyle}
                                >
                                  {/* Overview */}
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">{t("landing.featureDetail.pdf.mockup.overview")}</h4>
                                    <p className="text-[13px] text-gray-600 leading-relaxed">
                                      {t(summaryContent.overviewKey)}
                                      {/* Expandable multi-citation */}
                                      <AnimatePresence mode="wait">
                                        {!isOverviewCitationExpanded ? (
                                          // Collapsed: Show first page + "+1" button
                                          <motion.span
                                            key="collapsed"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="inline-flex items-center"
                                          >
                                            <CitationBadge
                                              page={summaryContent.overviewCitations[0]}
                                              onClick={() => handleHighlight("intro")}
                                            />
                                            <span
                                              className="text-[11px] ml-1 px-1.5 py-0.5 rounded-full cursor-pointer hover:bg-blue-100 transition-colors"
                                              style={{ backgroundColor: `${Color.themeBlue}10`, color: Color.themeBlue }}
                                              onClick={(e) => { e.stopPropagation(); setIsOverviewCitationExpanded(true); }}
                                            >
                                              +{summaryContent.overviewCitations.length - 1}
                                            </span>
                                          </motion.span>
                                        ) : (
                                          // Expanded: Show all citation badges
                                          <motion.span
                                            key="expanded"
                                            initial={{ opacity: 0, width: 0 }}
                                            animate={{ opacity: 1, width: "auto" }}
                                            exit={{ opacity: 0 }}
                                            className="inline-flex items-center gap-0.5"
                                          >
                                            {summaryContent.overviewCitations.map((page, idx) => (
                                              <CitationBadge
                                                key={idx}
                                                page={page}
                                                onClick={() => handleHighlight("intro")}
                                              />
                                            ))}
                                          </motion.span>
                                        )}
                                      </AnimatePresence>
                                    </p>
                                  </div>

                                  {/* Key Findings */}
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">{t("landing.featureDetail.pdf.mockup.keyFindings")}</h4>
                                    <ul className="space-y-3">
                                      {summaryContent.keyPoints.map((point, idx) => (
                                        <li
                                          key={idx}
                                          className="text-[13px] text-gray-600 leading-relaxed"
                                        >
                                          <span className="mr-1">•</span>
                                          {t(point.textKey)}
                                          <CitationBadge
                                            page={point.citation.page}
                                            onClick={() => handleHighlight(point.target)}
                                          />
                                          {"extra" in point.citation && (
                                            <span className="text-[11px] text-gray-400 ml-0.5">+{(point.citation as { page: string; extra: string }).extra}</span>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </motion.div>
                              ) : activeTab === "chat" ? (
                                /* AI Chat Tab with chat input bar */
                                <motion.div
                                  key="chat"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="h-full flex flex-col"
                                >
                                  {/* Chat messages area */}
                                  <div className="flex-1 p-3 space-y-2 overflow-y-auto" style={hideScrollbarStyle}>
                                    {chatStage === "sent" && (
                                      <>
                                        {/* User message - lighter theme blue bg, black text */}
                                        <motion.div
                                          className="flex "
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                        >
                                          <div
                                            className="px-3 py-1.5 rounded-2xl text-sm max-w-[85%]"
                                            style={{ backgroundColor: `${Color.themeBlue}20`, color: Color.textPrimary }}
                                          >
                                            Explain H(t) = H₀ · (1 + αlog(t))
                                          </div>
                                        </motion.div>
                                        {/* AI response - standardized */}
                                        {showAIChat && (
                                          <motion.div
                                            className="flex gap-2"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                          >
                                            <div
                                              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                              style={{ backgroundColor: `${Color.themeBlue}15` }}
                                            >
                                              <Icon icon="lucide:sparkles" className="text-xs" style={{ color: Color.themeBlue }} />
                                            </div>
                                            <div className="px-3 py-2 rounded-2xl text-sm leading-relaxed flex-1 text-gray-700" style={{ backgroundColor: Color.white, border: `1px solid ${Color.borderLight}` }}>
                                              {streamingIndex > 0 ? (
                                                <>
                                                  {aiResponseWords.slice(0, streamingIndex).join(" ")}
                                                  {streamingIndex < aiResponseWords.length && (
                                                    <span
                                                      className="inline-block w-0.5 h-3 ml-0.5 animate-pulse"
                                                      style={{ backgroundColor: Color.themeBlue }}
                                                    />
                                                  )}
                                                </>
                                              ) : (
                                                <div className="flex items-center gap-1 text-gray-400">
                                                  <div className="flex gap-0.5">
                                                    <div className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                                                    <div className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                                                    <div className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </motion.div>
                                        )}
                                      </>
                                    )}
                                    {chatStage === "input" && (
                                      <div className="h-full flex flex-col items-center justify-center text-center px-4">
                                        <div
                                          className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                                          style={{ backgroundColor: `${Color.themeBlue}15` }}
                                        >
                                          <Icon icon="lucide:sparkles" className="text-lg" style={{ color: Color.themeBlue }} />
                                        </div>
                                        <p className="text-sm text-gray-400">{t("landing.featureDetail.pdf.mockup.askAiPlaceholder")}</p>
                                      </div>
                                    )}
                                  </div>

                                </motion.div>
                              ) : activeTab === "sections" ? (
                                /* Sections Tab - clickable to highlight */
                                <motion.div
                                  key="sections"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="py-1 overflow-y-auto flex-1"
                                  style={hideScrollbarStyle}
                                >
                                  {/* Finding Key Points Loading */}
                                  <AnimatePresence mode="wait">
                                    {showFindingKeyPoints && (
                                      <motion.div
                                        initial={{ opacity: 0, height: "auto" }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0, overflow: "hidden" }}
                                        transition={{ duration: 0.2 }}
                                        className="flex flex-col items-center justify-center py-8 px-4"
                                      >
                                        <motion.div
                                          animate={{ rotate: 360 }}
                                          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                                          className="mb-3"
                                        >
                                          <Icon icon="lucide:search" className="text-2xl" style={{ color: Color.themeBlue }} />
                                        </motion.div>
                                        <p className="text-sm font-medium text-gray-700 mb-1">{t("modern.featureSections.pdf.findingKeyPoints") || "Finding key points..."}</p>
                                        <div className="flex gap-1 mt-2">
                                          {[0, 1, 2].map((i) => (
                                            <motion.div
                                              key={i}
                                              className="w-2 h-2 rounded-full"
                                              style={{ backgroundColor: Color.themeBlue }}
                                              animate={{ opacity: [0.3, 1, 0.3] }}
                                              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                                            />
                                          ))}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>

                                  {/* Sections - animated dash-in */}
                                  {!showFindingKeyPoints && sectionsContent.map((section, sIdx) => {
                                    const sectionId = `section-${sIdx}`;
                                    const isSectionVisible = visibleSections.includes(sectionId) || visibleSections.length === 0;

                                    if (!isSectionVisible && visibleSections.length > 0) return null;

                                    return (
                                    <motion.div
                                      key={sIdx}
                                      className="px-3 py-2"
                                      initial={visibleSections.length > 0 ? { opacity: 0, x: 30 } : { opacity: 1, x: 0 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ duration: 0.3, ease: "easeOut" }}
                                    >
                                      {/* Parent section - with bulge preview + ring wave on press */}
                                      <motion.div
                                        className="relative rounded-lg cursor-pointer"
                                        onClick={(e) => { e.stopPropagation(); handleHighlight(section.target, `section-${sIdx}`); }}
                                        animate={
                                          bulgingElement === `section-${sIdx}`
                                            ? { scale: [1, 1.05, 1.05], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 15px rgba(78, 164, 245, 0.35)", "0 0 15px rgba(78, 164, 245, 0.35)"] }
                                            : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                                        }
                                        transition={{ duration: bulgingElement === `section-${sIdx}` ? 0.5 : 0.2, ease: "easeOut" }}
                                        whileTap={{ scale: 0.97 }}
                                      >
                                        {/* Expanding ring border wave - VERY OBVIOUS */}
                                        <AnimatePresence>
                                          {clickedSection === `section-${sIdx}` && (
                                            <>
                                              {/* Ring 1 - immediate */}
                                              <motion.div
                                                className="absolute -inset-1 rounded-xl pointer-events-none"
                                                initial={{ opacity: 1, scale: 1 }}
                                                animate={{ opacity: 0, scale: 1.15 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.5, ease: "easeOut" }}
                                                style={{ border: `2px solid ${Color.themeBlue}` }}
                                              />
                                              {/* Ring 2 - delayed */}
                                              <motion.div
                                                className="absolute -inset-1 rounded-xl pointer-events-none"
                                                initial={{ opacity: 0.8, scale: 1 }}
                                                animate={{ opacity: 0, scale: 1.25 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                                                style={{ border: `2px solid ${Color.themeBlue}` }}
                                              />
                                              {/* Background flash */}
                                              <motion.div
                                                className="absolute inset-0 rounded-lg pointer-events-none"
                                                initial={{ opacity: 0.3 }}
                                                animate={{ opacity: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.4 }}
                                                style={{ backgroundColor: Color.themeBlue }}
                                              />
                                            </>
                                          )}
                                        </AnimatePresence>
                                        <div className="relative flex items-center gap-2 px-2 py-2.5 hover:bg-gray-50 rounded-lg transition-colors">
                                          <Icon
                                            icon={section.expanded ? "lucide:chevron-down" : "lucide:chevron-right"}
                                            width={14}
                                            height={14}
                                            className="text-gray-400 flex-shrink-0"
                                          />
                                          <span className="font-medium text-gray-900 text-[13px] leading-tight flex-1">
                                            {t(section.titleKey)}
                                          </span>
                                        </div>
                                      </motion.div>

                                      {/* Subsections - animated dash-in */}
                                      {section.expanded && section.subsections && (
                                        <div className="mt-1 ml-6 space-y-0">
                                          {section.subsections.map((sub, subIdx) => {
                                            const subId = `sub-${sIdx}-${subIdx}`;
                                            const isSubVisible = visibleSections.includes(subId) || visibleSections.length === 0;

                                            if (!isSubVisible && visibleSections.length > 0) return null;

                                            return (
                                            <motion.div
                                              key={subIdx}
                                              className="relative rounded-lg cursor-pointer"
                                              initial={visibleSections.length > 0 ? { opacity: 0, x: 20 } : { opacity: 1, x: 0 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              transition={{ duration: 0.25, ease: "easeOut", delay: subIdx * 0.05 }}
                                              onClick={(e) => { e.stopPropagation(); handleHighlight(sub.target, `sub-${sIdx}-${subIdx}`); }}
                                            >
                                              <motion.div
                                                animate={
                                                  bulgingElement === `sub-${sIdx}-${subIdx}`
                                                    ? { scale: [1, 1.05, 1.05], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 15px rgba(78, 164, 245, 0.35)", "0 0 15px rgba(78, 164, 245, 0.35)"] }
                                                    : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                                                }
                                                transition={{ duration: bulgingElement === `sub-${sIdx}-${subIdx}` ? 0.5 : 0.2, ease: "easeOut" }}
                                                whileTap={{ scale: 0.97 }}
                                              >
                                                {/* Expanding ring border wave */}
                                                <AnimatePresence>
                                                  {clickedSection === `sub-${sIdx}-${subIdx}` && (
                                                    <>
                                                      <motion.div
                                                        className="absolute -inset-1 rounded-xl pointer-events-none"
                                                        initial={{ opacity: 1, scale: 1 }}
                                                        animate={{ opacity: 0, scale: 1.15 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                                        style={{ border: `2px solid ${Color.themeBlue}` }}
                                                      />
                                                      <motion.div
                                                        className="absolute inset-0 rounded-lg pointer-events-none"
                                                        initial={{ opacity: 0.3 }}
                                                        animate={{ opacity: 0 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.4 }}
                                                        style={{ backgroundColor: Color.themeBlue }}
                                                      />
                                                    </>
                                                  )}
                                                </AnimatePresence>
                                                <div className="relative px-2 py-2 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2">
                                                  <Icon
                                                    icon="lucide:star"
                                                    width={12}
                                                    height={12}
                                                    className="flex-shrink-0"
                                                    style={{ color: getDifficultyColor(sub.difficulty) }}
                                                  />
                                                  <span className="font-medium text-gray-800 text-[12px] leading-tight">
                                                    {t(sub.titleKey)}
                                                  </span>
                                                </div>
                                              </motion.div>
                                            </motion.div>
                                          );})}
                                        </div>
                                      )}
                                    </motion.div>
                                  );})}
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              </div>{/* close scale wrapper */}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
  );
};

export default FeatureSectionPDF;
